import { Injectable } from '@angular/core';
import { ApolloQueryResult, FetchResult } from '@apollo/client/core';
import type { GetSensorsQuery } from '@uigy/generated/graphql';
import {
  ApproveSensorGQL,
  ApproveSensorMutation,
  BulkEditSensorsGQL,
  BulkEditSensorsMutation,
  Capability,
  CheckForSensorUpdatesGQL,
  CheckForSensorUpdatesMutation,
  DeleteSensorGQL,
  DeleteSensorMutation,
  EditSensorGQL,
  EditSensorMutation,
  EditSensorMutationVariables,
  GetSensorsGQL,
  JobFragment,
  PauseSensorGQL,
  PauseSensorMutation,
  ResumeSensorGQL,
  ResumeSensorMutation,
  SensorsBulkAction,
  SensorStatus,
  UpdateSensorGQL,
  UpdateSensorMutation,
} from '@uigy/generated/graphql';
import { CapabilitiesService } from '@uigy/shared/capabilities/capabilities.service';
import { ToasterType } from '@uigy/shared/components/toaster/enums/toaster-type.enum';
import { ToasterService } from '@uigy/shared/components/toaster/services/toaster.service';
import { TimeUnits } from '@uigy/shared/enums/timeUnits.enum';
import { LanguageService } from '@uigy/shared/services/language.service';
import Utils from '@uigy/utils';
import { Observable } from 'rxjs';
import { map, tap } from 'rxjs/operators';

type SensorNode = GetSensorsQuery['sensors']['nodes'][number];

@Injectable({
  providedIn: 'root'
})
export class SensorsService {
  private TEXTS = this.languageService.texts;

  constructor(private getSensorsGQL: GetSensorsGQL,
              private deleteSensorGQL: DeleteSensorGQL,
              private pauseSensorGQL: PauseSensorGQL,
              private approveSensorGQL: ApproveSensorGQL,
              private resumeSensorGQL: ResumeSensorGQL,
              private editSensorGQL: EditSensorGQL,
              private updateSensorGQL:UpdateSensorGQL,
              private checkForSensorUpdatesGQL:CheckForSensorUpdatesGQL,
              private toasterService: ToasterService,
              private languageService: LanguageService,
              private capabilitiesService: CapabilitiesService,
              private bulkEditSensorsGQL: BulkEditSensorsGQL,
  ) {
  }

  public watchSensors(): Observable<SensorNode[]> {
    return this.getSensorsGQL.watch(undefined, {pollInterval: TimeUnits.ThirtySec}).valueChanges.pipe(
      map(
        (response: ApolloQueryResult<GetSensorsQuery>): SensorNode[] => response?.data?.sensors?.nodes || []
      )
    );
  }


  public fetchSensors(): Observable<SensorNode[]> {
    return this.getSensorsGQL.fetch().pipe(
      map(
        (response: ApolloQueryResult<GetSensorsQuery>): SensorNode[] => response?.data?.sensors?.nodes || []
      )
    );
  }

  public deleteSensor(sensor: SensorNode): Observable<SensorNode | undefined> {
    return this.deleteSensorGQL.mutate({id: sensor.id}).pipe(
      map(
        (response: FetchResult<DeleteSensorMutation>): SensorNode | undefined => response?.data?.deleteSensor
      ),
      tap(
        () => {
          this.toasterService.openToaster({
            type: ToasterType.Success,
            message: this.languageService.getTranslationTyped('settings.system_config.sensors.delete_sensor.notifications.success', {sensorName: sensor.name})
          });
        },
        () => {
          this.toasterService.openToaster({
              type: ToasterType.Error,
              message: this.languageService.getTranslationTyped('settings.system_config.sensors.delete_sensor.notifications.failure', {sensorName: sensor.name})
            });
        }
      ),
    );
  }

  public pauseSensor(sensor: SensorNode): Observable<SensorNode | undefined> {
    return this.pauseSensorGQL.mutate({id: sensor.id}).pipe(
      map(
        (response: FetchResult<PauseSensorMutation>): SensorNode | undefined => response?.data?.pauseSensor
      ),
      tap(
        () => {
          this.toasterService.openToaster({
            type: ToasterType.Success,
            message: this.languageService.getTranslationTyped('settings.system_config.sensors.pause_sensor.notifications.success', {sensorName: sensor.name})
          });
        },
        () => {
          this.toasterService.openToaster({
            type: ToasterType.Error,
            message: this.languageService.getTranslationTyped('settings.system_config.sensors.pause_sensor.notifications.failure', {sensorName: sensor.name})
          });
        }
      ),
    );
  }

  public approveSensor(sensor: SensorNode): Observable<SensorNode | undefined> {
    return this.approveSensorGQL.mutate({id: sensor.id}).pipe(
      map(
        (response: FetchResult<ApproveSensorMutation>): SensorNode | undefined => response?.data?.approveSensor
      )
    );
  }

  public resumeSensor(sensor: SensorNode): Observable<SensorNode | undefined> {
    return this.resumeSensorGQL.mutate({id: sensor.id}).pipe(
      map(
        (response: FetchResult<ResumeSensorMutation>): SensorNode | undefined => response?.data?.resumeSensor
      ),
      tap(
        () => {
          this.toasterService.openToaster({
            type: ToasterType.Success,
            message: this.languageService.getTranslationTyped('settings.system_config.sensors.resume_sensor.notifications.success', {sensorName: sensor.name})
          });
        },
        () => {
          this.toasterService.openToaster({
            type: ToasterType.Error,
            message: this.languageService.getTranslationTyped('settings.system_config.sensors.resume_sensor.notifications.failure', {sensorName: sensor.name})
          });
        }
      ),
    );
  }


  public editSensor(sensor: EditSensorMutationVariables): Observable<SensorNode | undefined> {
    return this.editSensorGQL.mutate({
      id: sensor.id,
      name: sensor.name,
      active: sensor.active,
      activeNetworks: sensor.activeNetworks
    }).pipe(
      map(
        (response: FetchResult<EditSensorMutation>): SensorNode | undefined => response?.data?.editSensor
      ),
      tap(
        () => {
          this.toasterService.openToaster({
            type: ToasterType.Success,
            message: this.TEXTS.settings.system_config.sensors.edit_sensor.notifications.success
          });
        },
        () => {
          this.toasterService.openToaster({
            type: ToasterType.Error,
            message: this.TEXTS.settings.system_config.sensors.edit_sensor.notifications.failure
          });
        }
      ),
    );
  }

  public updateSensors(sensors: SensorNode[]): Observable<JobFragment> {
    const ids: string[] = sensors.map(sensor => sensor.id);
    return this.updateSensorGQL.mutate({ ids }).pipe(
      Utils.fetchResultOperator(),
      map((response: UpdateSensorMutation) => response.updateSensor),
    );
  }

  public checkForUpdates(): Observable<JobFragment> {
    return this.checkForSensorUpdatesGQL.mutate().pipe(
      Utils.fetchResultOperator(),
      map((response: CheckForSensorUpdatesMutation) => response.checkForSensorUpdates),
    );
  }

  public isUpdateAvailable(sensor: SensorNode): boolean {
    return sensor.systemUpdatesExist || sensor.stockdogUpdateExists;
  }

  public canUpdate(sensor: SensorNode): boolean {
    return [SensorStatus.Connected, SensorStatus.Paused].includes(sensor.status);
  }

  public isCapableToUpdateSensors(): boolean {
    return this.capabilitiesService.capableOf(Capability.WriteSensors);
  }

  public bulkEditSensorQuery(ids: string[], action: SensorsBulkAction): Observable<JobFragment> {
    return this.bulkEditSensorsGQL.mutate({ ids, action }).pipe(
      Utils.fetchResultOperator(),
      map((response: BulkEditSensorsMutation) => response.bulkEditSensors),
    );
  }

  public bulkEditSensorsSuccessMessage(action: SensorsBulkAction, count: number): string {
    switch (action) {
      case SensorsBulkAction.EnableActive:
        return this.languageService.getTranslationTyped('settings.system_config.sensors.update_sensor.active_query_enabled_successful', { count });
      case SensorsBulkAction.DisableActive:
        return this.languageService.getTranslationTyped('settings.system_config.sensors.update_sensor.active_query_disabled_successful', { count });
      case SensorsBulkAction.Pause:
        return this.languageService.getTranslationTyped('settings.system_config.sensors.update_sensor.bulk_pause_successful', { count });
      case SensorsBulkAction.Resume:
        return this.languageService.getTranslationTyped('settings.system_config.sensors.update_sensor.bulk_resume_successful', { count });
      default:
        return '';

    }
  }
}
